import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

// A select with a search box inside its list. The client list runs to hundreds of
// partners, and a native <select> cannot be searched beyond typing the first letters.
const ClientCombobox = ({ options, value, onChange, disabled, placeholder, emptyLabel }) => {
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState('')
	const [highlighted, setHighlighted] = useState(0)
	const rootRef = useRef(null)
	const searchRef = useRef(null)
	const listRef = useRef(null)

	const term = search.trim().toLowerCase()
	const matching = term
		? options.filter(option => option.searchText.toLowerCase().includes(term))
		: options
	const selected = options.find(option => option.value === value)

	const close = () => {
		setOpen(false)
		setSearch('')
	}

	const choose = (option) => {
		close()
		if (option.value !== value) {
			onChange(option.value)
		}
	}

	// A click anywhere else closes the list, as a native select would.
	useEffect(() => {
		if (!open) {
			return undefined
		}
		const onMouseDown = (e) => {
			if (rootRef.current && !rootRef.current.contains(e.target)) {
				close()
			}
		}
		document.addEventListener('mousedown', onMouseDown)
		return () => document.removeEventListener('mousedown', onMouseDown)
	}, [open])

	useEffect(() => {
		if (open && searchRef.current) {
			searchRef.current.focus()
		}
	}, [open])

	// Start on the current pick when opening, and on the first hit once a search narrows the list.
	useEffect(() => {
		if (!open) {
			return
		}
		const index = term ? 0 : options.findIndex(option => option.value === value)
		setHighlighted(Math.max(index, 0))
	}, [open, term]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const item = listRef.current && listRef.current.children[highlighted]
		if (item && item.scrollIntoView) {
			item.scrollIntoView({ block: 'nearest' })
		}
	}, [highlighted])

	const handleKeyDown = (e) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault()
			setHighlighted(index => Math.min(index + 1, matching.length - 1))
		} else if (e.key === 'ArrowUp') {
			e.preventDefault()
			setHighlighted(index => Math.max(index - 1, 0))
		} else if (e.key === 'Enter') {
			e.preventDefault()
			if (matching[highlighted]) {
				choose(matching[highlighted])
			}
		} else if (e.key === 'Escape') {
			e.preventDefault()
			close()
		}
	}

	return (
		<div className="client-combobox" ref={rootRef}>
			<button
				type="button"
				className="client-selector client-combobox-trigger"
				onClick={() => (open ? close() : setOpen(true))}
				disabled={disabled}
				aria-haspopup="listbox"
				aria-expanded={open}
			>
				<span className="client-combobox-value">{selected ? selected.label : placeholder}</span>
				<span className="client-combobox-caret" aria-hidden="true">▾</span>
			</button>
			{open && (
				<div className="client-combobox-panel">
					<input
						ref={searchRef}
						type="search"
						className="amount-input client-combobox-search"
						value={search}
						onChange={e => setSearch(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder="Search clients"
						aria-label="Search clients"
					/>
					{matching.length === 0 ? (
						<div className="client-combobox-empty">
							{term ? `No clients match “${search.trim()}”` : emptyLabel}
						</div>
					) : (
						<ul className="client-combobox-list" role="listbox" ref={listRef}>
							{matching.map((option, index) => (
								<li
									key={option.value}
									role="option"
									aria-selected={option.value === value}
									className={[
										'client-combobox-option',
										index === highlighted ? 'is-highlighted' : '',
										option.value === value ? 'is-selected' : '',
									].join(' ')}
									onMouseEnter={() => setHighlighted(index)}
									// mousedown, not click, so the choice lands before the search input blurs
									onMouseDown={(e) => {
										e.preventDefault()
										choose(option)
									}}
								>
									{option.label}
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</div>
	)
}

ClientCombobox.propTypes = {
	options: PropTypes.arrayOf(PropTypes.shape({
		value: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		searchText: PropTypes.string.isRequired,
	})).isRequired,
	value: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	placeholder: PropTypes.string,
	emptyLabel: PropTypes.string,
}

ClientCombobox.defaultProps = {
	disabled: false,
	placeholder: '-- Select a client --',
	emptyLabel: 'No clients available',
}

export default ClientCombobox
